/**
 * Snapshots tab — Phase 6 import / export.
 *
 * Layout:
 *   1. Export — toggle profiles / offlineQueue, click to download a
 *      v1 station snapshot.
 *   2. Import — drag-drop OR file picker; on (cpid, wsUrl) conflict the
 *      tab swaps to a conflict dialog with Replace / Save-as-copy / Cancel.
 *   3. Status — last report summary + validation errors when present.
 */
import { useCallback, useEffect, useState } from 'react';
import { useStationStore } from '@/state/useStationStore';
import {
  ConflictResolution,
  useSnapshotStore,
  type ExportOverrides,
} from '@/state/useSnapshotStore';
import { toast } from '@/state/useToastStore';
import { Toggle } from '@/ui/components/Toggle';
import { FileDropZone } from '@/ui/components/FileDropZone';
import { formatDateTime, pluralize } from '@/lib/format';

export function SnapshotsTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const stationCpid = useStationStore((s) => s.cpid);
  const hasOpenStation = stationCpid !== '';
  const exportNow = useSnapshotStore((s) => s.exportNow);
  const importFromText = useSnapshotStore((s) => s.importFromText);
  const resolvePending = useSnapshotStore((s) => s.resolvePending);
  const cancelPending = useSnapshotStore((s) => s.cancelPending);
  const status = useSnapshotStore((s) => s.status);
  const pending = useSnapshotStore((s) => s.pending);
  const lastReport = useSnapshotStore((s) => s.lastReport);
  const lastError = useSnapshotStore((s) => s.lastError);
  const validationErrors = useSnapshotStore((s) => s.validationErrors);
  const audit = useSnapshotStore((s) => s.audit);
  const refreshAudit = useSnapshotStore((s) => s.refreshAudit);

  const [includeProfiles, setIncludeProfiles] = useState(true);
  const [includeOffline, setIncludeOffline] = useState(true);
  const [includeDeviceModel, setIncludeDeviceModel] = useState(true);
  const [includeLocalAuth, setIncludeLocalAuth] = useState(true);
  const [copyCpid, setCopyCpid] = useState('');
  const [copyWsUrl, setCopyWsUrl] = useState('');

  const handleExport = (): void => {
    const overrides: ExportOverrides = {
      include: {
        chargingProfiles: includeProfiles,
        offlineQueue: includeOffline,
        deviceModel: includeDeviceModel,
        localAuthList: includeLocalAuth,
      },
    };
    void exportNow(stationId, overrides).then(() => {
      toast('Snapshot downloaded');
    });
  };

  // NIT-013 — toast on every successful import (after the conflict
  // resolution path completes). The `lastReport` slice flips to a
  // truthy object exactly once per successful import.
  const lastReportId = lastReport ? `${lastReport.stationId}:${lastReport.replaced ? 'r' : 'n'}` : null;
  useEffect(() => {
    if (lastReport) {
      toast(
        `Imported ${lastReport.cpid} — ${lastReport.counts.configurationKeys} keys, ${lastReport.counts.chargingProfiles} profiles, ${lastReport.counts.offlineQueue} offline sessions, ${lastReport.counts.variableValues} variables, ${lastReport.counts.localAuthEntries} auth entries`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastReportId]);

  // Cleanup A (8f.9) — load the snapshots audit history for the open station.
  useEffect(() => {
    if (hasOpenStation) void refreshAudit(stationId);
  }, [hasOpenStation, refreshAudit, stationId]);

  const consumeFile = useCallback(
    (file: File): void => {
      void file.text().then((text) => {
        void importFromText(text);
      });
    },
    [importFromText],
  );

  return (
    <div
      role="tabpanel"
      id="tab-panel-snapshots"
      aria-labelledby="tab-snapshots"
      className="space-y-4 p-4"
      data-testid="snapshots-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Export</h2>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5 text-slate-300">
            <Toggle
              checked={includeProfiles}
              onChange={setIncludeProfiles}
              label="Include charging profiles"
              testId="export-include-profiles"
            />
            Include charging profiles
          </span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <Toggle
              checked={includeOffline}
              onChange={setIncludeOffline}
              label="Include offline queue"
              testId="export-include-offline"
            />
            Include offline queue
          </span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <Toggle
              checked={includeDeviceModel}
              onChange={setIncludeDeviceModel}
              label="Include device model (OCPP 2.x)"
              testId="export-include-device-model"
            />
            Include device model
          </span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <Toggle
              checked={includeLocalAuth}
              onChange={setIncludeLocalAuth}
              label="Include local auth list"
              testId="export-include-local-auth"
            />
            Include local auth list
          </span>
          <button
            type="button"
            onClick={handleExport}
            className="rounded bg-primary-hover px-3 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="snapshot-export"
          >
            Download snapshot
          </button>
        </div>
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Import</h2>
        <FileDropZone
          accept="application/json,.json"
          onFile={consumeFile}
          prompt="Drop a snapshot JSON here, or"
          testId="snapshot-dropzone"
          pickTestId="snapshot-import-pick"
          inputTestId="snapshot-import-input"
        />
      </section>

      {pending && status === 'awaiting-conflict' ? (
        <section
          className="rounded border border-amber-700 bg-amber-950/40 p-3 text-xs text-amber-100"
          data-testid="snapshot-conflict"
        >
          <p className="mb-2 font-medium">Identity conflict.</p>
          <p className="mb-2">
            A station with cpid={' '}
            <span className="font-mono">{pending.conflict.cpid}</span> and the same WS URL
            already exists. Pick a resolution:
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                void resolvePending({ resolution: ConflictResolution.Replace });
              }}
              className="self-start rounded bg-rose-700 px-2 py-1 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
              data-testid="snapshot-conflict-replace"
            >
              Replace existing station
            </button>
            <div className="rounded bg-slate-900/60 p-2">
              <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
                Save as copy
              </p>
              <div className="flex flex-wrap items-center gap-1">
                <input
                  type="text"
                  placeholder="New cpid"
                  value={copyCpid}
                  onChange={(e) => setCopyCpid(e.target.value)}
                  className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                  data-testid="snapshot-conflict-cpid"
                />
                <input
                  type="text"
                  placeholder="Or new wsUrl"
                  value={copyWsUrl}
                  onChange={(e) => setCopyWsUrl(e.target.value)}
                  className="w-72 rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                  data-testid="snapshot-conflict-wsurl"
                />
                <button
                  type="button"
                  onClick={() => {
                    void resolvePending({
                      resolution: ConflictResolution.SaveAsCopy,
                      ...(copyCpid.trim() !== '' ? { cpid: copyCpid.trim() } : {}),
                      ...(copyWsUrl.trim() !== '' ? { wsUrl: copyWsUrl.trim() } : {}),
                    });
                  }}
                  disabled={copyCpid.trim() === '' && copyWsUrl.trim() === ''}
                  className="rounded bg-sky-700 px-2 py-1 text-sky-50 hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="snapshot-conflict-saveascopy"
                >
                  Save as copy
                </button>
              </div>
            </div>
            {/* FEAT-007 — Apply the snapshot's contents to the open
                station, keeping its identity. Only meaningful when a
                station is open. */}
            <button
              type="button"
              disabled={!hasOpenStation}
              onClick={() => {
                void resolvePending({
                  resolution: ConflictResolution.ApplyToOpen,
                  targetStationId: stationId,
                  include: {
                    chargingProfiles: includeProfiles,
                    offlineQueue: includeOffline,
                    deviceModel: includeDeviceModel,
                    localAuthList: includeLocalAuth,
                  },
                });
              }}
              title={
                hasOpenStation
                  ? 'Replace this station’s configuration / profiles / queue with the snapshot’s'
                  : 'Open a station first'
              }
              className="self-start rounded bg-primary-hover px-2 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="snapshot-conflict-apply-to-open"
            >
              Apply to currently open station
            </button>
            <button
              type="button"
              onClick={cancelPending}
              className="self-start rounded bg-slate-700 px-2 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="snapshot-conflict-cancel"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      {lastReport ? (
        <section
          className="rounded border border-primary-base bg-primary-base/30 p-3 text-xs text-primary-on"
          data-testid="snapshot-report"
        >
          Imported{' '}
          <span className="font-mono" data-testid="snapshot-report-cpid">
            {lastReport.cpid}
          </span>{' '}
          ({lastReport.replaced ? 'replaced' : 'new'}) ·{' '}
          {lastReport.counts.configurationKeys} keys ·{' '}
          {lastReport.counts.chargingProfiles} profiles ·{' '}
          {lastReport.counts.offlineQueue} offline sessions ·{' '}
          {lastReport.counts.variableValues} variables ·{' '}
          {lastReport.counts.localAuthEntries} auth entries.
        </section>
      ) : null}

      {validationErrors.length > 0 ? (
        <section
          className="rounded border border-rose-700 bg-rose-950/30 p-3 text-xs text-rose-100"
          data-testid="snapshot-validation-errors"
        >
          <p className="mb-1 font-medium">Snapshot did not pass validation:</p>
          <ul className="list-inside list-disc">
            {validationErrors.map((e) => (
              <li key={e} className="font-mono text-[11px]">
                {e}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {lastError && validationErrors.length === 0 ? (
        <section
          className="rounded border border-rose-700 bg-rose-950/30 p-3 text-xs text-rose-100"
          data-testid="snapshot-error"
        >
          {lastError}
        </section>
      ) : null}

      {/* Cleanup A (8f.9) — audit history list. Always rendered when a
          station is open; auto-refreshes after every export/import. */}
      {hasOpenStation ? (
        <section
          className="rounded border border-slate-700 bg-slate-900/60 p-3"
          data-testid="snapshot-audit"
        >
          <header className="mb-2 flex items-baseline justify-between">
            <h2 className="text-sm font-medium text-slate-100">History</h2>
            <span className="text-[10px] text-slate-500" data-testid="snapshot-audit-count">
              {pluralize(audit.length, 'entry', 'entries')}
            </span>
          </header>
          {audit.length === 0 ? (
            <p className="text-[11px] text-slate-500" data-testid="snapshot-audit-empty">
              No exports or imports yet. The audit log fills as you use this tab.
            </p>
          ) : (
            <ul className="space-y-1 text-xs" data-testid="snapshot-audit-list">
              {audit.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 rounded bg-slate-950/40 px-2 py-1"
                  data-testid={`snapshot-audit-${entry.id}`}
                >
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${kindTone(entry.kind)}`}
                  >
                    {entry.kind}
                  </span>
                  <span className="font-mono text-slate-300">{entry.cpid}</span>
                  <span className="ml-auto text-[10px] text-slate-500" title={new Date(entry.createdAt).toISOString()}>
                    {formatDateTime(entry.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}

function kindTone(kind: 'export' | 'import' | 'import-applied'): string {
  switch (kind) {
    case 'export':
      return 'bg-primary-base/30 text-primary-on';
    case 'import':
      return 'bg-sky-800/70 text-sky-50';
    case 'import-applied':
      return 'bg-amber-800/70 text-amber-50';
  }
}
